import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetFeedbackLoopData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertCircle, Flag, Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Bar,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function FeedbackLoopTabContent({
  date,
  domain,
}: {
  date: string;
  domain: string;
}) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetFeedbackLoopData({
    domain,
    startDate,
    endDate,
  });

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  // Add table for flagged identifiers if they exist
  const showIdentifiersTable =
    data?.statistics.flaggedIdentifiers.length ?? 0 > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Current Spam Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {data?.statistics.currentSpamRate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </div>
              <AlertCircle
                className={`h-5 w-5 ${
                  (data?.statistics.currentSpamRate ?? 0) <= 0.1
                    ? "text-blue-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Flagged Identifiers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {data?.statistics.identifierCount}
                </div>
                <p className="text-xs text-muted-foreground">Current count</p>
              </div>
              <Flag
                className={`h-5 w-5 ${
                  data?.statistics.identifierCount === 0
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center gap-2">
              <Badge
                className={`shadow-none ${
                  data?.statistics.status === "Clean"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                } hover:bg-opacity-80`}
              >
                {data?.statistics.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {data?.statistics.status === "Clean"
                  ? "No issues detected"
                  : "Spam rate above threshold"}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-4">
          <CardTitle>Spam Feedback Loop</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data?.chartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number, name: string) => [
                    name === "spamRate" ? `${value.toFixed(1)}%` : value,
                    name === "spamRate" ? "Spam Rate" : "Identifier Count",
                  ]}
                  labelFormatter={(label) =>
                    format(new Date(label), "MMM dd, yyyy")
                  }
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(new Date(date), "MMM dd")}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  yAxisId="left"
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 1, 2, 3, 4]}
                  domain={[0, 4]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  domain={[0, "auto"]}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid #e5e7eb",
                  }}
                  formatter={(value: number, name: string) => [
                    name === "spamRate" ? `${value.toFixed(1)}%` : value,
                    name === "spamRate"
                      ? "Feedback Loop spam rate"
                      : "Feedback Loop identifier count",
                  ]}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="spamRate"
                  name="Feedback Loop spam rate"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
                <Bar
                  yAxisId="right"
                  dataKey="identifierCount"
                  name="Feedback Loop identifier count"
                  fill="#EF4444"
                  radius={[2, 2, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {showIdentifiersTable ? (
            <div className="mt-6">
              <h3 className="mb-4 text-sm font-medium">Flagged Identifiers</h3>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Identifier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Spam Ratio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.statistics.flaggedIdentifiers.map(
                      (identifier, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {identifier.identifier}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {identifier.spamRatio.toFixed(1)}%
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              No flagged Identifiers for {format(endDate, "MMMM d")}.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { FeedbackLoopTabContent };

function FeedbackLoopTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <FeedbackLoopTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute(
  "/_private/postmaster/tabs/feedback-loop-tab"
)({
  component: FeedbackLoopTabRouteComponent,
});
